import { PRICING_MODEL } from '@monorepo/shared/schemas/common'
import type { ColumnDef } from '@tanstack/react-table'
import {
  BarChart3,
  CheckSquare,
  Edit,
  GitBranch,
  Minus,
  MoreHorizontal,
  Target,
  TrendingUp,
} from 'lucide-react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Input } from '@/shared/components/ui/input'
import type { EditableCharge } from '../../types/customerDetail.types'

interface ChargeTableProps {
  charges: EditableCharge[]
  chargeType: 'fixed' | 'recurring' | 'agents' | 'features'
  onChargeUpdate: (chargeId: string, newPrice: number) => void
  onEditCharge?: (charge: EditableCharge) => void
}

export function ChargeTable({
  charges,
  chargeType,
  onChargeUpdate,
  onEditCharge,
}: ChargeTableProps) {
  // Helper function to get the starting price based on pricing model
  const getStartingPrice = (charge: EditableCharge): number => {
    if (charge.pricingModel === PRICING_MODEL.package && charge.packagePrice) {
      return charge.packagePrice
    }
    if (
      (charge.pricingModel === PRICING_MODEL.tiered ||
        charge.pricingModel === PRICING_MODEL.volume) &&
      charge.tiers &&
      charge.tiers.length > 0
    ) {
      // API returns unit_price in tiers, handle both formats
      const firstTier = charge.tiers[0]

      // Try different price field formats
      if (firstTier.perUnitPrice && firstTier.perUnitPrice > 0) {
        return firstTier.perUnitPrice
      } else if (firstTier.unit_price) {
        return parseFloat(String(firstTier.unit_price))
      } else if (firstTier.unitPrice) {
        return parseFloat(String(firstTier.unitPrice))
      }

      return 0
    }
    return charge.unitPrice || 0
  }

  const getPricingIcon = (pricingModel?: string) => {
    switch (pricingModel) {
      case PRICING_MODEL.tiered:
        return TrendingUp
      case PRICING_MODEL.volume:
        return BarChart3
      default:
        return Minus
    }
  }

  const renderPriceCell = (charge: EditableCharge) => {
    const isPricingModelComplex =
      charge.pricingModel === PRICING_MODEL.tiered ||
      charge.pricingModel === PRICING_MODEL.volume
    const isPackage = charge.pricingModel === PRICING_MODEL.package
    const PricingIcon = getPricingIcon(charge.pricingModel)
    const startingPrice = getStartingPrice(charge)

    return (
      <div className='flex items-center gap-2'>
        {isPackage ? (
          <div className='flex items-center gap-2'>
            <PricingIcon className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>
              ${startingPrice} / {charge.packageSize?.toLocaleString() || 0}{' '}
              units
            </span>
          </div>
        ) : isPricingModelComplex ? (
          <div className='flex items-center gap-2'>
            <PricingIcon className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>starts at ${startingPrice}/unit</span>
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <PricingIcon className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>${startingPrice}</span>
          </div>
        )}
        {charge.isEdited && (
          <Badge variant='outline' className='text-xs'>
            Modified
          </Badge>
        )}
      </div>
    )
  }

  const renderPricingModelCell = (charge: EditableCharge) => {
    const model = charge.pricingModel || PRICING_MODEL.flat
    return (
      <div className='text-sm capitalize'>
        {model === PRICING_MODEL.flat ? 'Fixed' : model}
      </div>
    )
  }

  const createChargeColumns = (
    chargeType: 'fixed' | 'recurring' | 'agents' | 'features'
  ): ColumnDef<EditableCharge>[] => {
    if (chargeType === 'agents') {
      return [
        {
          accessorKey: 'agentName',
          header: 'Agent Name',
          size: 140,
          cell: ({ row }) => (
            <div className='font-medium'>
              {row.original.agentName || row.original.name}
            </div>
          ),
        },
        {
          accessorKey: 'signalName',
          header: 'Signal Name',
          size: 150,
          cell: ({ row }) => (
            <div className='font-medium text-sm'>
              {row.original.signalName || row.original.signalId}
            </div>
          ),
        },
        {
          accessorKey: 'kind',
          header: 'Signal Type',
          size: 120,
          cell: ({ row }) => {
            const getTypeIcon = (type?: string) => {
              switch (type?.toLowerCase()) {
                case 'task':
                  return CheckSquare
                case 'workflow':
                  return GitBranch
                case 'outcome':
                  return Target
                default:
                  return CheckSquare
              }
            }
            const TypeIcon = getTypeIcon(row.original.kind)

            return (
              <div className='flex items-center gap-2'>
                <TypeIcon className='w-4 h-4 text-muted-foreground' />
                <span className='text-sm capitalize'>
                  {row.original.kind || 'task'}
                </span>
              </div>
            )
          },
        },
        {
          accessorKey: 'pricingModel',
          header: 'Pricing Model',
          size: 100,
          cell: ({ row }) => renderPricingModelCell(row.original),
        },
        {
          accessorKey: 'billingCycle',
          header: 'Billing Period',
          size: 100,
          cell: ({ row }) => {
            const charge = row.original
            const billingCycle =
              charge.billingCycle || charge.billing_cycle || 'monthly'
            return <div className='text-sm capitalize'>{billingCycle}</div>
          },
        },
        {
          accessorKey: 'unitPrice',
          header: 'Price',
          size: 160,
          cell: ({ row }) => renderPriceCell(row.original),
        },
        {
          accessorKey: 'minCommitment',
          header: 'Min',
          size: 100,
          cell: ({ row }) => {
            const charge = row.original
            const minCommit =
              charge.minimum_commitment_units ||
              charge.minCommitment ||
              charge.minimum_commitment ||
              charge.minimumCommitment ||
              0
            return <div className='text-sm'>{minCommit}</div>
          },
        },
        {
          accessorKey: 'includedInPlan',
          header: 'Included',
          size: 120,
          cell: ({ row }) => {
            const charge = row.original
            const includedUnits =
              charge.includedUnits ||
              charge.included_units ||
              charge.included_quantity ||
              charge.includedQuantity ||
              0
            return (
              <div className='flex items-center justify-between'>
                <span className='text-sm'>{includedUnits}</span>
                {onEditCharge && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='ghost' size='sm' className='h-6 w-6 p-0'>
                        <MoreHorizontal className='w-4 h-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={() => onEditCharge(charge)}>
                        <Edit className='w-4 h-4 mr-2' />
                        Edit Agent Pricing
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )
          },
        },
      ]
    }

    if (chargeType === 'features') {
      return [
        {
          accessorKey: 'name',
          header: 'Feature Name',
          size: 250,
          cell: ({ row }) => (
            <div className='font-medium'>{row.original.name}</div>
          ),
        },
        {
          accessorKey: 'pricingModel',
          header: 'Pricing Model',
          size: 100,
          cell: ({ row }) => renderPricingModelCell(row.original),
        },
        {
          accessorKey: 'billingCycle',
          header: 'Billing Period',
          size: 100,
          cell: ({ row }) => {
            const charge = row.original
            const billingCycle =
              charge.billingCycle || charge.billing_cycle || 'monthly'
            return <div className='text-sm capitalize'>{billingCycle}</div>
          },
        },
        {
          accessorKey: 'unitPrice',
          header: 'Price',
          size: 160,
          cell: ({ row }) => renderPriceCell(row.original),
        },
        {
          accessorKey: 'minCommitment',
          header: 'Min',
          size: 120,
          cell: ({ row }) => {
            const charge = row.original
            const minCommit =
              charge.minimum_commitment_units ||
              charge.minCommitment ||
              charge.minimum_commitment ||
              charge.minimumCommitment ||
              0
            return <div className='text-sm'>{minCommit}</div>
          },
        },
        {
          accessorKey: 'includedInPlan',
          header: 'Included',
          cell: ({ row }) => {
            const charge = row.original
            const includedUnits =
              charge.includedUnits ||
              charge.included_units ||
              charge.included_quantity ||
              charge.includedQuantity ||
              0
            return (
              <div className='flex items-center justify-between'>
                <span className='text-sm'>{includedUnits}</span>
                {onEditCharge && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='ghost' size='sm' className='h-6 w-6 p-0'>
                        <MoreHorizontal className='w-4 h-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem onClick={() => onEditCharge(charge)}>
                        <Edit className='w-4 h-4 mr-2' />
                        Edit Price
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )
          },
        },
      ]
    }

    // Default columns for fixed and recurring charges
    return [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 400,
        cell: ({ row }) => (
          <div>
            <div className='font-medium'>{row.original.name}</div>
            <div className='text-sm text-muted-foreground'>
              {row.original.description}
            </div>
            {(row.original.includedUnits ?? 0) > 0 && (
              <div className='text-xs text-green-600 mt-1'>
                {(row.original.includedUnits ?? 0).toLocaleString()} included
                units
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'unitPrice',
        header: 'Price',
        cell: ({ row }) => {
          const charge = row.original
          return (
            <div className='flex items-center gap-2'>
              <Input
                type='number'
                step='0.001'
                min='0'
                value={charge.unitPrice}
                onChange={e =>
                  onChargeUpdate(charge.id, parseFloat(e.target.value) || 0)
                }
                className='w-24'
              />
              <span className='text-sm text-muted-foreground'>
                {charge.type === 'usage'
                  ? '/100 units'
                  : charge.type === 'seat'
                    ? '/seat'
                    : ''}
              </span>
              {charge.isEdited && (
                <Badge variant='outline' className='text-xs'>
                  Modified
                </Badge>
              )}
            </div>
          )
        },
      },
    ]
  }

  return (
    <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
      <EnhancedTable
        data={charges}
        columns={createChargeColumns(chargeType).map(col => ({
          ...col,
          enableSorting: false,
        }))}
        height='400px'
        className='bg-transparent'
      />
    </div>
  )
}
